import { useEffect, useState, useMemo, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Spinner } from '../components/ui/spinner';
import { MapLocationButton } from '../components/MapLocationButton';
import { Car, Building, ArrowUpFromLine, MapPin, Phone, Clock, Route, Navigation, Layers, Bus, Train } from 'lucide-react';
import { AccessibilityMapIcon, ElevatorIcon, WCIcon } from '../components/icons/AccessibilityIcons';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import axios from 'axios';
import 'leaflet/dist/leaflet.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fix Leaflet marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom icons for each category
const createColoredIcon = (color) => new L.Icon({
  iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${color}.png`,
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
});

const icons = {
  parking: createColoredIcon('blue'),
  aseos: createColoredIcon('green'),
  aseos_ostomizados: createColoredIcon('gold'),
  rampas: createColoredIcon('orange'),
  ascensores: createColoredIcon('violet'),
  edificios: createColoredIcon('red'),
  bus: createColoredIcon('yellow'),
  tram: createColoredIcon('green'),
};

const categoryColors = {
  parking: '#3b82f6',
  aseos: '#22c55e',
  aseos_ostomizados: '#eab308',
  rampas: '#f97316',
  ascensores: '#8b5cf6',
  itinerarios: '#6b7280',
  edificios: '#ef4444',
  transport: '#f59e0b',
};

const createClusterIcon = (cluster, color) => {
  const count = cluster.getChildCount();
  const size = count >= 100 ? 50 : count >= 10 ? 40 : 30;
  return L.divIcon({
    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;display:flex;align-items:center;justify-content:center;color:white;font-weight:bold;font-size:12px;box-shadow:0 4px 12px rgba(0,0,0,0.3);background-color:${color};">${count}</div>`,
    className: 'custom-cluster-icon',
    iconSize: L.point(size, size, true),
  });
};

export function AccessibilityPage() {
  const { language } = useLanguage();
  
  // Data state for each category (lazy loaded)
  const [data, setData] = useState({
    parking: [],
    toilets: [],
    ostomyToilets: [],
    ramps: [],
    elevators: [],
    routes: [],
    buildings: [],
    transport: [],
  });
  
  // Track which categories have been loaded
  const [loadedCategories, setLoadedCategories] = useState({});
  
  // Track which category is currently loading
  const [loadingCategory, setLoadingCategory] = useState(null);
  
  // Layer visibility state - only parking is visible by default
  const [visibleLayers, setVisibleLayers] = useState({
    parking: true,
    toilets: false,
    ostomyToilets: false,
    ramps: false,
    elevators: false,
    routes: false,
    buildings: false,
    transport: false,
  });

  const t = {
    title: language === 'es' ? 'Mapa de Accesibilidad' : 'Accessibility Map',
    subtitle: language === 'es' ? 'Recursos accesibles en Tenerife - Activa las capas para comparar ubicaciones' : 'Accessible resources in Tenerife - Toggle layers to compare locations',
    parking: language === 'es' ? 'Plazas PMR' : 'Disabled Parking',
    toilets: language === 'es' ? 'Aseos Accesibles' : 'Accessible Toilets',
    ostomyToilets: language === 'es' ? 'Aseos Ostomizados' : 'Ostomy Toilets',
    ramps: language === 'es' ? 'Rampas' : 'Ramps',
    elevators: language === 'es' ? 'Ascensores' : 'Elevators',
    routes: language === 'es' ? 'Itinerarios Peatonales' : 'Pedestrian Routes',
    buildings: language === 'es' ? 'Edificios Públicos' : 'Public Buildings',
    transport: language === 'es' ? 'Transporte Público' : 'Public Transport',
    showing: language === 'es' ? 'Mostrando' : 'Showing',
    results: language === 'es' ? 'resultados' : 'results',
    loading: language === 'es' ? 'Cargando...' : 'Loading...',
    openInGoogleMaps: language === 'es' ? 'Abrir en Google Maps' : 'Open in Google Maps',
    selectLayers: language === 'es' ? 'Selecciona las capas a mostrar:' : 'Select layers to display:',
    clickToLoad: language === 'es' ? 'Clic para cargar' : 'Click to load',
  };

  const categories = [
    { key: 'parking', label: t.parking, icon: Car, color: categoryColors.parking, tipo: 'parking', endpoint: '/accessibility/parking' },
    { key: 'toilets', label: t.toilets, icon: null, customIcon: 'toilet', color: categoryColors.aseos, tipo: 'aseos', endpoint: '/accessibility/toilets' },
    { key: 'ostomyToilets', label: t.ostomyToilets, icon: null, customIcon: 'ostomy', color: categoryColors.aseos_ostomizados, tipo: 'aseos_ostomizados', endpoint: '/accessibility/ostomy-toilets' },
    { key: 'ramps', label: t.ramps, icon: ArrowUpFromLine, color: categoryColors.rampas, tipo: 'rampas', endpoint: '/accessibility/ramps' },
    { key: 'elevators', label: t.elevators, icon: null, customIcon: 'elevator', color: categoryColors.ascensores, tipo: 'ascensores', endpoint: '/accessibility/elevators' },
    { key: 'routes', label: t.routes, icon: Route, color: categoryColors.itinerarios, tipo: 'itinerarios', endpoint: '/accessibility/routes', isPolyline: true },
    { key: 'buildings', label: t.buildings, icon: Building, color: categoryColors.edificios, tipo: 'edificios', endpoint: '/accessibility/buildings' },
    { key: 'transport', label: t.transport, icon: Bus, color: categoryColors.transport, tipo: 'transport', endpoint: '/transport' },
  ];

  // Helper to render custom icons for layer toggles
  const renderCategoryIcon = (cat, className = "w-4 h-4") => {
    if (cat.customIcon === 'toilet' || cat.customIcon === 'ostomy') {
      return <WCIcon className={className} color="currentColor" />;
    } else if (cat.customIcon === 'elevator') {
      return <ElevatorIcon className={className} color="currentColor" />;
    } else if (cat.icon) {
      const Icon = cat.icon;
      return <Icon className={className} />;
    }
    return null;
  };

  // Fetch data for a specific category
  const fetchCategoryData = useCallback(async (categoryKey) => {
    if (loadedCategories[categoryKey]) return; // Already loaded
    
    const category = categories.find(c => c.key === categoryKey);
    if (!category) return;
    
    setLoadingCategory(categoryKey);
    try {
      const response = await axios.get(`${API}${category.endpoint}`);
      let categoryData = response.data || [];
      
      // Handle transport data structure (it returns {bus_stops: [], tram_stops: []})
      if (categoryKey === 'transport') {
        const busStops = (response.data?.bus_stops || []).map(stop => ({
          ...stop,
          nombre: stop.parada_nombre || stop.nombre,
          tipo: 'Guagua'
        }));
        const tramStops = (response.data?.tram_stops || []).map(stop => ({
          ...stop,
          nombre: stop.parada_nombre || stop.nombre,
          tipo: 'Tranvía'
        }));
        categoryData = [...busStops, ...tramStops];
      }
      
      setData(prev => ({ ...prev, [categoryKey]: categoryData }));
      setLoadedCategories(prev => ({ ...prev, [categoryKey]: true }));
    } catch (error) {
      console.error(`Error fetching ${categoryKey}:`, error);
      setData(prev => ({ ...prev, [categoryKey]: [] }));
      setLoadedCategories(prev => ({ ...prev, [categoryKey]: true }));
    } finally {
      setLoadingCategory(null);
    }
  }, [loadedCategories]);

  // Load parking data on mount (default selected layer)
  useEffect(() => {
    fetchCategoryData('parking');
  }, []);

  // Toggle layer visibility and load data if needed
  const toggleLayer = async (key) => {
    const newVisibility = !visibleLayers[key];
    setVisibleLayers(prev => ({ ...prev, [key]: newVisibility }));
    
    // If turning on and data not loaded, fetch it
    if (newVisibility && !loadedCategories[key]) {
      await fetchCategoryData(key);
    }
  };

  // Count visible items
  const visibleCount = useMemo(() => {
    let count = 0;
    Object.keys(visibleLayers).forEach(key => {
      if (visibleLayers[key] && loadedCategories[key]) {
        count += (data[key] || []).length;
      }
    });
    return count;
  }, [visibleLayers, data, loadedCategories]);

  // Get markers for a category
  const getMarkers = (key) => {
    if (!visibleLayers[key] || !loadedCategories[key]) return [];
    const categoryData = data[key] || [];
    return categoryData.filter(item => item.lat && item.lon);
  };

  // Prepare polylines for routes
  const polylines = useMemo(() => {
    if (!visibleLayers.routes || !loadedCategories.routes) return [];
    return (data.routes || [])
      .filter(item => item.coordinates && item.coordinates.length > 0)
      .map(item => ({
        ...item,
        positions: item.coordinates.map(coord => [coord[1], coord[0]])
      }));
  }, [data.routes, visibleLayers.routes, loadedCategories.routes]);

  const centerPosition = [28.2916, -16.5291];

  // Check if any layer is loading
  const isAnyLoading = loadingCategory !== null;

  // Render popup content
  const renderPopupContent = (item, categoryKey) => {
    const category = categories.find(c => c.key === categoryKey);
    return (
      <div className="font-['Outfit'] min-w-[200px] max-w-[300px]">
        {item.foto && (
          <img 
            src={item.foto} 
            alt={item.nombre}
            className="w-full h-32 object-cover rounded mb-2"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}
        
        <h3 className="font-bold text-[#252a34] mb-1 text-sm">{item.nombre}</h3>
        <Badge 
          className="text-xs mb-2 text-white"
          style={{ backgroundColor: category?.color || '#6b7280' }}
        >
          {category?.label}
        </Badge>
        
        <div className="text-xs text-[#252a34]/70 space-y-1 mt-2">
          {item.ubicacion && (
            <div className="flex items-start gap-1">
              <MapPin className="w-3 h-3 flex-shrink-0 mt-0.5" />
              <span>{item.ubicacion}</span>
            </div>
          )}
          {item.direccion && (
            <div className="flex items-start gap-1">
              <MapPin className="w-3 h-3 flex-shrink-0 mt-0.5" />
              <span>{item.direccion}</span>
            </div>
          )}
          {item.municipio && (
            <p className="text-[#252a34]/60 ml-4">{item.municipio}</p>
          )}
          {item.apertura && (
            <p className="text-[#252a34]/60">
              {language === 'es' ? 'Apertura' : 'Access'}: {item.apertura}
            </p>
          )}
          {item.horario && (
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3 flex-shrink-0" />
              <span>{item.horario}</span>
            </div>
          )}
          {item.telefono && (
            <div className="flex items-center gap-1">
              <Phone className="w-3 h-3 flex-shrink-0" />
              <a href={`tel:${item.telefono}`} className="text-[#00599c] hover:underline">
                {item.telefono}
              </a>
            </div>
          )}
          {item.pendiente && (
            <p className="text-[#252a34]/60">
              {language === 'es' ? 'Pendiente' : 'Slope'}: {item.pendiente}%
            </p>
          )}
          {item.ancho && (
            <p className="text-[#252a34]/60">
              {language === 'es' ? 'Ancho' : 'Width'}: {item.ancho}m
            </p>
          )}
        </div>
        
        <a
          href={`https://www.google.com/maps/search/?api=1&query=${item.lat},${item.lon}`}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 flex items-center justify-center gap-1 w-full px-3 py-1.5 bg-[#1a73e8] text-xs font-medium rounded hover:bg-[#1557b0] transition-colors"
          style={{ color: 'white' }}
        >
          <Navigation className="w-3 h-3" style={{ color: 'white' }} />
          {t.openInGoogleMaps}
        </a>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          <AccessibilityMapIcon className="w-5 h-5" color="#00599c" />
          <span className="text-sm font-medium text-[#00599c] uppercase tracking-wider">
            {language === 'es' ? 'Datos Abiertos' : 'Open Data'}
          </span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#252a34] font-['Outfit'] mb-2" data-testid="accessibility-title">
          {t.title}
        </h1>
        <p className="text-lg text-[#252a34]/70">{t.subtitle}</p>
      </div>

      {/* Layer Controls */}
      <Card className="mb-6 border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Layers className="w-5 h-5 text-[#00599c]" />
            <span className="font-medium text-[#252a34]">{t.selectLayers}</span>
            {isAnyLoading && (
              <span className="text-xs text-[#00599c] flex items-center gap-1">
                <Spinner size="sm" /> {t.loading}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => {
              const isActive = visibleLayers[cat.key];
              const isLoaded = loadedCategories[cat.key];
              const isLoading = loadingCategory === cat.key;
              const count = isLoaded ? (data[cat.key] || []).length : null;
              
              return (
                <button
                  key={cat.key}
                  onClick={() => toggleLayer(cat.key)}
                  disabled={isLoading}
                  data-testid={`layer-${cat.key}`}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all border ${
                    isActive
                      ? 'text-white shadow-md border-transparent'
                      : 'bg-white text-[#252a34] border-slate-200 hover:border-[#00599c] hover:shadow-sm'
                  } ${isLoading ? 'opacity-70' : ''}`}
                  style={isActive ? { backgroundColor: cat.color } : {}}
                >
                  {isLoading ? (
                    <Spinner size="sm" className="w-4 h-4" />
                  ) : (
                    <span className={isActive ? 'text-white' : ''}>
                      {renderCategoryIcon(cat, `w-4 h-4`)}
                    </span>
                  )}
                  <span>{cat.label}</span>
                  <Badge 
                    variant={isActive ? "secondary" : "outline"} 
                    className={`text-xs ml-1 ${isActive ? 'bg-white/20 text-white' : ''}`}
                  >
                    {isLoading ? '...' : (count !== null ? count.toLocaleString() : '?')}
                  </Badge>
                </button>
              );
            })}
          </div>
          <p className="text-xs text-[#252a34]/50 mt-3">
            {t.showing} <strong>{visibleCount.toLocaleString()}</strong> {t.results}
          </p>
        </CardContent>
      </Card>

      {/* Single Map with All Layers */}
      <Card className="overflow-hidden border-slate-200 mb-6">
        <div className="h-[500px] md:h-[600px]">
          {loadingCategory === 'parking' && !loadedCategories.parking ? (
            <div className="flex flex-col items-center justify-center h-full bg-slate-50">
              <Spinner size="lg" />
              <p className="mt-4 text-[#252a34]/60 text-sm font-medium">{t.loading}</p>
            </div>
          ) : (
            <MapContainer center={centerPosition} zoom={10} style={{ height: '100%', width: '100%' }} className="z-0">
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MapLocationButton language={language} />
              
              {/* Parking Layer */}
              {visibleLayers.parking && loadedCategories.parking && getMarkers('parking').length > 0 && (
                <MarkerClusterGroup 
                  chunkedLoading 
                  iconCreateFunction={(cluster) => createClusterIcon(cluster, categoryColors.parking)} 
                  maxClusterRadius={50}
                >
                  {getMarkers('parking').map((item, idx) => (
                    <Marker key={`parking-${idx}`} position={[item.lat, item.lon]} icon={icons.parking}>
                      <Popup>{renderPopupContent(item, 'parking')}</Popup>
                    </Marker>
                  ))}
                </MarkerClusterGroup>
              )}

              {/* Toilets Layer */}
              {visibleLayers.toilets && loadedCategories.toilets && getMarkers('toilets').length > 0 && (
                <MarkerClusterGroup 
                  chunkedLoading 
                  iconCreateFunction={(cluster) => createClusterIcon(cluster, categoryColors.aseos)} 
                  maxClusterRadius={50}
                >
                  {getMarkers('toilets').map((item, idx) => (
                    <Marker key={`toilets-${idx}`} position={[item.lat, item.lon]} icon={icons.aseos}>
                      <Popup>{renderPopupContent(item, 'toilets')}</Popup>
                    </Marker>
                  ))}
                </MarkerClusterGroup>
              )}

              {/* Ostomy Toilets Layer */}
              {visibleLayers.ostomyToilets && loadedCategories.ostomyToilets && getMarkers('ostomyToilets').length > 0 && (
                <MarkerClusterGroup 
                  chunkedLoading 
                  iconCreateFunction={(cluster) => createClusterIcon(cluster, categoryColors.aseos_ostomizados)} 
                  maxClusterRadius={50}
                >
                  {getMarkers('ostomyToilets').map((item, idx) => (
                    <Marker key={`ostomy-${idx}`} position={[item.lat, item.lon]} icon={icons.aseos_ostomizados}>
                      <Popup>{renderPopupContent(item, 'ostomyToilets')}</Popup>
                    </Marker>
                  ))}
                </MarkerClusterGroup>
              )}

              {/* Ramps Layer */}
              {visibleLayers.ramps && loadedCategories.ramps && getMarkers('ramps').length > 0 && (
                <MarkerClusterGroup 
                  chunkedLoading 
                  iconCreateFunction={(cluster) => createClusterIcon(cluster, categoryColors.rampas)} 
                  maxClusterRadius={50}
                >
                  {getMarkers('ramps').map((item, idx) => (
                    <Marker key={`ramps-${idx}`} position={[item.lat, item.lon]} icon={icons.rampas}>
                      <Popup>{renderPopupContent(item, 'ramps')}</Popup>
                    </Marker>
                  ))}
                </MarkerClusterGroup>
              )}

              {/* Elevators Layer */}
              {visibleLayers.elevators && loadedCategories.elevators && getMarkers('elevators').length > 0 && (
                <MarkerClusterGroup 
                  chunkedLoading 
                  iconCreateFunction={(cluster) => createClusterIcon(cluster, categoryColors.ascensores)} 
                  maxClusterRadius={50}
                >
                  {getMarkers('elevators').map((item, idx) => (
                    <Marker key={`elevators-${idx}`} position={[item.lat, item.lon]} icon={icons.ascensores}>
                      <Popup>{renderPopupContent(item, 'elevators')}</Popup>
                    </Marker>
                  ))}
                </MarkerClusterGroup>
              )}

              {/* Buildings Layer */}
              {visibleLayers.buildings && loadedCategories.buildings && getMarkers('buildings').length > 0 && (
                <MarkerClusterGroup 
                  chunkedLoading 
                  iconCreateFunction={(cluster) => createClusterIcon(cluster, categoryColors.edificios)} 
                  maxClusterRadius={50}
                >
                  {getMarkers('buildings').map((item, idx) => (
                    <Marker key={`buildings-${idx}`} position={[item.lat, item.lon]} icon={icons.edificios}>
                      <Popup>{renderPopupContent(item, 'buildings')}</Popup>
                    </Marker>
                  ))}
                </MarkerClusterGroup>
              )}

              {/* Routes Layer (Polylines) */}
              {visibleLayers.routes && loadedCategories.routes && polylines.map((item, idx) => (
                <Polyline
                  key={`route-${idx}`}
                  positions={item.positions}
                  pathOptions={{ color: categoryColors.itinerarios, weight: 3, opacity: 0.7 }}
                >
                  <Popup>
                    <div className="font-['Outfit'] min-w-[200px] max-w-[300px]">
                      {item.foto && (
                        <img 
                          src={item.foto} 
                          alt={item.nombre}
                          className="w-full h-32 object-cover rounded mb-2"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                      <h3 className="font-bold text-[#252a34] mb-1 text-sm">{item.nombre}</h3>
                      <Badge className="text-xs mb-2 text-white" style={{ backgroundColor: categoryColors.itinerarios }}>
                        {t.routes}
                      </Badge>
                      <div className="text-xs text-[#252a34]/60 space-y-1 mt-2">
                        {item.ancho && <p>{language === 'es' ? 'Ancho' : 'Width'}: {item.ancho}m</p>}
                        {item.pendiente && <p>{language === 'es' ? 'Pendiente' : 'Slope'}: {item.pendiente}%</p>}
                      </div>
                      {item.positions && item.positions.length > 0 && (
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${item.positions[0][0]},${item.positions[0][1]}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-3 flex items-center justify-center gap-1 w-full px-3 py-1.5 bg-[#1a73e8] text-xs font-medium rounded hover:bg-[#1557b0] transition-colors"
                          style={{ color: 'white' }}
                        >
                          <Navigation className="w-3 h-3" style={{ color: 'white' }} />
                          {t.openInGoogleMaps}
                        </a>
                      )}
                    </div>
                  </Popup>
                </Polyline>
              ))}

              {/* Transport Layer */}
              {visibleLayers.transport && loadedCategories.transport && getMarkers('transport').length > 0 && (
                <MarkerClusterGroup 
                  chunkedLoading 
                  iconCreateFunction={(cluster) => createClusterIcon(cluster, categoryColors.transport)} 
                  maxClusterRadius={60}
                >
                  {getMarkers('transport').map((item, idx) => (
                    <Marker 
                      key={`transport-${idx}`} 
                      position={[item.lat, item.lon]} 
                      icon={item.tipo === 'Tranvía' ? icons.tram : icons.bus}
                    >
                      <Popup>
                        <div className="font-['Outfit'] min-w-[200px] max-w-[300px]">
                          <h3 className="font-bold text-[#252a34] mb-1 text-sm">{item.nombre}</h3>
                          <Badge 
                            className="text-xs mb-2 text-white"
                            style={{ backgroundColor: item.tipo === 'Tranvía' ? '#22c55e' : categoryColors.transport }}
                          >
                            {item.tipo === 'Tranvía' ? (
                              <><Train className="w-3 h-3 mr-1 inline" />{language === 'es' ? 'Tranvía' : 'Tram'}</>
                            ) : (
                              <><Bus className="w-3 h-3 mr-1 inline" />{language === 'es' ? 'Guagua' : 'Bus'}</>
                            )}
                          </Badge>
                          <div className="text-xs text-[#252a34]/70 space-y-1 mt-2">
                            {item.municipio && (
                              <div className="flex items-start gap-1">
                                <MapPin className="w-3 h-3 flex-shrink-0 mt-0.5" />
                                <span>{item.municipio}</span>
                              </div>
                            )}
                            {item.lineas && item.lineas.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {item.lineas.slice(0, 6).map((linea, i) => (
                                  <span key={i} className="px-1.5 py-0.5 bg-slate-100 rounded text-[10px] font-medium">
                                    {linea}
                                  </span>
                                ))}
                                {item.lineas.length > 6 && (
                                  <span className="px-1.5 py-0.5 bg-slate-100 rounded text-[10px]">+{item.lineas.length - 6}</span>
                                )}
                              </div>
                            )}
                          </div>
                          {item.parada_url && (
                            <a 
                              href={item.parada_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="mt-2 block text-xs text-[#00599c] hover:underline font-medium"
                            >
                              {language === 'es' ? 'Ver horarios →' : 'View schedules →'}
                            </a>
                          )}
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${item.lat},${item.lon}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-3 flex items-center justify-center gap-1 w-full px-3 py-1.5 bg-[#1a73e8] text-xs font-medium rounded hover:bg-[#1557b0] transition-colors"
                            style={{ color: 'white' }}
                          >
                            <Navigation className="w-3 h-3" style={{ color: 'white' }} />
                            {t.openInGoogleMaps}
                          </a>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MarkerClusterGroup>
              )}
            </MapContainer>
          )}
        </div>
      </Card>

      {/* Legend */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <h3 className="font-medium text-[#252a34] mb-3">{language === 'es' ? 'Leyenda' : 'Legend'}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {categories.map((cat) => (
              <div key={cat.key} className="flex items-center gap-2">
                <div 
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: cat.color }}
                />
                <span className="text-xs text-[#252a34]/70">{cat.label}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
